import { useEffect, useRef, useState } from "react";
import { apiFetchStream } from "@/api";
import type { ProgressLine } from "@/types";

export interface StreamedLoadState<T> {
  data: T | null;
  progress: ProgressLine | null;
  loading: boolean;
  error: string | null;
}

/**
 * Runs an NDJSON streamed fetch (apiFetchStream) whenever `url` changes,
 * exposing live progress. Doesn't use an AbortController — apiFetchStream
 * doesn't support one, and the old main.ts never needed real cancellation
 * either, just a "latest call wins" guard, reproduced here via a generation
 * counter. `url` of `null` skips the fetch (e.g. while a prerequisite like
 * the site list hasn't resolved yet).
 */
export function useStreamedLoad<T>(url: string | null): StreamedLoadState<T> {
  const [state, setState] = useState<StreamedLoadState<T>>({
    data: null,
    progress: null,
    loading: url != null,
    error: null,
  });
  const gen = useRef(0);

  useEffect(() => {
    if (url == null) {
      setState({ data: null, progress: null, loading: false, error: null });
      return;
    }
    const myGen = ++gen.current;
    setState((s) => ({ ...s, loading: true, error: null, progress: null }));
    apiFetchStream<T>(url, (p) => {
      if (gen.current !== myGen) return;
      setState((s) => ({ ...s, progress: p }));
    })
      .then((data) => {
        if (gen.current !== myGen) return;
        setState({ data, progress: null, loading: false, error: null });
      })
      .catch((e: unknown) => {
        if (gen.current !== myGen) return;
        setState({ data: null, progress: null, loading: false, error: (e as Error).message });
      });
  }, [url]);

  return state;
}
